import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ColorPicker } from '@/components/ui/ColorPicker'
import { useUpdatePartyBasics } from '@/hooks/useUpdatePartyBasics'
import type { Party, UpdatePartyBasicsInput } from '@/types'

interface EditPartyModalProps {
  party: Party
  open: boolean
  onOpenChange: (open: boolean) => void
}

const NAME_MIN_LENGTH = 3
const NAME_MAX_LENGTH = 50
const ABBR_MIN_LENGTH = 2
const ABBR_MAX_LENGTH = 6
const SLOGAN_MAX_LENGTH = 100

export function EditPartyModal({ party, open, onOpenChange }: EditPartyModalProps) {
  // Render the form content only when open, so it resets on each open
  if (!open) {
    return null
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <EditPartyForm party={party} onOpenChange={onOpenChange} />
    </Dialog>
  )
}

interface EditPartyFormProps {
  party: Party
  onOpenChange: (open: boolean) => void
}

function EditPartyForm({ party, onOpenChange }: EditPartyFormProps) {
  const [name, setName] = useState(party.name)
  const [abbreviation, setAbbreviation] = useState(party.abbreviation)
  const [color, setColor] = useState(party.color)
  const [slogan, setSlogan] = useState(party.slogan)

  const { updateBasics, isUpdating, error } = useUpdatePartyBasics(party.id)

  const isValidHex = /^#[0-9A-Fa-f]{6}$/.test(color)

  const nameError = name.length < NAME_MIN_LENGTH
    ? `Name must be at least ${NAME_MIN_LENGTH} characters`
    : name.length > NAME_MAX_LENGTH
      ? `Name must be at most ${NAME_MAX_LENGTH} characters`
      : null

  const abbrError = abbreviation.length < ABBR_MIN_LENGTH
    ? `Abbreviation must be at least ${ABBR_MIN_LENGTH} characters`
    : abbreviation.length > ABBR_MAX_LENGTH
      ? `Abbreviation must be at most ${ABBR_MAX_LENGTH} characters`
      : null

  const colorError = !isValidHex ? 'Invalid hex color format' : null

  const sloganError = slogan.length > SLOGAN_MAX_LENGTH
    ? `Slogan must be at most ${SLOGAN_MAX_LENGTH} characters`
    : null

  const hasChanges =
    name !== party.name ||
    abbreviation !== party.abbreviation ||
    color !== party.color ||
    slogan !== party.slogan

  const isValid = !nameError && !abbrError && !colorError && !sloganError

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!isValid || !hasChanges) return

    const updates: UpdatePartyBasicsInput = {}
    if (name !== party.name) updates.name = name
    if (abbreviation !== party.abbreviation) updates.abbreviation = abbreviation
    if (color !== party.color) updates.color = color
    if (slogan !== party.slogan) updates.slogan = slogan

    updateBasics(updates, {
      onSuccess: () => {
        onOpenChange(false)
      },
    })
  }

  return (
    <DialogContent className="sm:max-w-md">
      <form onSubmit={handleSubmit}>
        <DialogHeader>
          <DialogTitle>Edit Party</DialogTitle>
          <DialogDescription>
            Update your party's name, abbreviation, color, and slogan.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Party Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Progressive Alliance"
              maxLength={NAME_MAX_LENGTH}
            />
            {nameError && (
              <p className="text-sm text-destructive">{nameError}</p>
            )}
          </div>

          {/* Abbreviation */}
          <div className="space-y-2">
            <Label htmlFor="abbreviation">Abbreviation</Label>
            <Input
              id="abbreviation"
              value={abbreviation}
              onChange={(e) => setAbbreviation(e.target.value.toUpperCase())}
              placeholder="PA"
              maxLength={ABBR_MAX_LENGTH}
              className="w-24 font-mono"
            />
            {abbrError && (
              <p className="text-sm text-destructive">{abbrError}</p>
            )}
          </div>

          {/* Color */}
          <div className="space-y-2">
            <Label>Party Color</Label>
            <ColorPicker value={color} onChange={setColor} />
            {colorError && (
              <p className="text-sm text-destructive">{colorError}</p>
            )}
          </div>

          {/* Slogan */}
          <div className="space-y-2">
            <Label htmlFor="slogan">Slogan (optional)</Label>
            <Input
              id="slogan"
              value={slogan}
              onChange={(e) => setSlogan(e.target.value)}
              placeholder="Building tomorrow, together"
              maxLength={SLOGAN_MAX_LENGTH}
            />
            <p className="text-xs text-muted-foreground">
              {slogan.length}/{SLOGAN_MAX_LENGTH} characters
            </p>
            {sloganError && (
              <p className="text-sm text-destructive">{sloganError}</p>
            )}
          </div>

          {error && (
            <p className="text-sm text-destructive">
              Failed to update party: {error.message}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!isValid || !hasChanges || isUpdating}
          >
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}
